// =============================================================================
// Home page content — edit values here to change copy across the homepage.
// Re-deploy (or merge a PR on main once auto-deploy is on) to see it live.
// =============================================================================

export const homeContent = {
  // --- SEO (browser tab + search engines) ---
  seo: {
    title: "Lacrea — Sites web sur mesure pour entrepreneurs et PME",
    description:
      "Studio web spécialisé dans la création de sites internet 100% sur mesure pour entrepreneurs et PME. Pas de templates. Devis gratuit sous 48h.",
  },

  // --- Hero (top section) ---
  hero: {
    eyebrow: "Studio web — sur mesure",
    titlePrefix: "Nous créons des sites web ",
    titleHighlight: "sur mesure",
    titleSuffix: ".",
    subtitle:
      "Efficacité maximale. Racontez votre histoire à travers un site sur-mesure qui met en valeur vos produits et votre savoir-faire.",
    badges: [
      "À partir de 49,90€/mois",
      "Sans apport",
      "Livré en 2 semaines",
    ],
    primaryCta: "Démarrer mon projet",
    contactEmail: "contact@lacrea.fr",
  },

  // --- "Pourquoi du sur-mesure" section ---
  why: {
    eyebrow: "Pourquoi Lacrea",
    title: "Pourquoi du sur-mesure ?",
    subtitle:
      "Parce qu'un site générique vous fait ressembler à tout le monde — et un site copié-collé ne convertit pas.",
    points: [
      {
        title: "Conçu pour votre activité",
        body:
          "Chaque page, chaque mot et chaque interaction sont pensés pour vos clients — pas pour un template générique partagé par 10 000 sites.",
      },
      {
        title: "Performant et bien référencé",
        body:
          "Code propre, chargement rapide, SEO travaillé dès la conception. Votre site est un outil commercial, pas une carte de visite.",
      },
      {
        title: "Vous restez propriétaire",
        body:
          "Pas d'abonnement piégeur, pas de plugin payant, pas de plateforme qui vous garde en otage. Votre site vous appartient.",
      },
    ],
    ctaLabel: "Demander un devis gratuit",
  },

  // --- "Comment ça se passe" section ---
  process: {
    eyebrow: "Notre méthode",
    title: "Comment ça se passe",
    subtitle:
      "Un processus simple, transparent, sans jargon. Devis gratuit sous 48h.",
    steps: [
      {
        n: "1",
        title: "On échange",
        body:
          "Un appel de 30 min pour comprendre votre activité, vos clients et vos objectifs.",
      },
      {
        n: "2",
        title: "On conçoit et on code",
        body:
          "Maquettes validées sous 1 semaine, développement sur mesure derrière.",
      },
      {
        n: "3",
        title: "On livre et on accompagne",
        body:
          "Mise en ligne, formation, et un mois de suivi inclus pour ajuster.",
      },
    ],
    ctaLabel: "Démarrer mon projet",
  },

  // --- Pricing section ---
  pricing: {
    eyebrow: "Nos formules",
    title: "Une formule pour chaque projet",
    subtitle:
      "Trois formules claires, sans apport, sans engagement. Vous restez propriétaire de votre site, on s'occupe du reste.",
    tiers: [
      {
        name: "Essentiel",
        tagline: "Pour se lancer avec une vitrine pro",
        price: "49,90€",
        priceSuffix: "/mois",
        priceNote: "Sans apport · 24 mois min.",
        features: [
          "Site vitrine sur mesure (jusqu'à 5 pages)",
          "Design unique, mobile-first",
          "Hébergement & maintenance inclus",
          "SEO de base + formulaire de contact",
          "Livraison en 2 semaines",
          "Support par email",
        ],
        cta: "Démarrer avec Essentiel",
        featured: false,
      },
      {
        name: "Pro",
        tagline: "Pour convertir et grandir",
        price: "99€",
        priceSuffix: "/mois",
        priceNote: "Sans apport · 24 mois min.",
        features: [
          "Tout Essentiel, plus :",
          "Jusqu'à 15 pages + blog / CMS light",
          "SEO local avancé + Google Business",
          "Intégration analytics & suivi conversions",
          "Refonte design incluse chaque année",
          "Support prioritaire sous 24h",
        ],
        cta: "Choisir Pro",
        featured: true,
        badge: "Le plus choisi",
      },
      {
        name: "Sur-Mesure",
        tagline: "Projets ambitieux & e-commerce",
        price: "à partir de 149€",
        priceSuffix: "/mois",
        priceNote: "Ou devis projet ponctuel",
        features: [
          "Tout Pro, plus :",
          "Pages illimitées · multi-langue",
          "E-commerce, intégrations IA, API",
          "Fonctionnalités personnalisées",
          "SEO expert + stratégie de contenu",
          "Suivi trimestriel dédié",
        ],
        cta: "Parler de mon projet",
        featured: false,
      },
    ],
    footnote:
      "Besoin d'un modèle one-shot (paiement unique) ? Disponible à partir de 800€ — mentionnez-le dans votre message.",
  },

  // --- Blog teaser section ---
  blog: {
    title: "Ressources & conseils",
    subtitle:
      "Nos articles pour vous aider à mieux comprendre le web, le SEO et la création de sites.",
    viewAllLabel: "Voir tous les articles",
  },

  // --- Project Wizard (replaces the simple contact form) ---
  // Linear wizard: 5 qualifying questions + 1 contact step. Editing here
  // is enough to tweak copy, add/remove options, reorder steps. Keep the
  // final step `kind: "contact"` — it is the one that submits the form.
  projectWizard: {
    title: "Parlons de votre projet",
    subtitle:
      "6 questions rapides pour préparer votre devis. Réponse sous 48h par email.",
    stepLabel: "Étape",
    stepOf: "sur",
    backLabel: "Retour",
    nextLabel: "Suivant",
    steps: [
      {
        id: "type",
        kind: "radio",
        label: "Quel type de projet ?",
        required: true,
        options: [
          { value: "vitrine", label: "Site vitrine", sub: "Présenter mon activité" },
          { value: "ecommerce", label: "E-commerce", sub: "Vendre en ligne" },
          { value: "application", label: "Application web", sub: "Fonctionnalités sur mesure" },
          { value: "refonte", label: "Refonte d'un site existant", sub: "Repartir sur une base moderne" },
          { value: "autre", label: "Autre", sub: "Vous préciserez dans la note libre" },
        ],
      },
      {
        id: "size",
        kind: "radio",
        label: "Quelle taille approximative ?",
        required: true,
        options: [
          { value: "small", label: "≤ 5 pages", sub: "Site vitrine simple" },
          { value: "medium", label: "Une quinzaine de pages", sub: "Blog, services détaillés" },
          { value: "large", label: "Beaucoup / illimité", sub: "Catalogue, e-commerce étendu" },
        ],
      },
      {
        id: "sector",
        kind: "radio",
        label: "Votre secteur d'activité",
        required: true,
        options: [
          { value: "commerce", label: "Commerce / boutique" },
          { value: "restauration", label: "Restauration / hôtellerie" },
          { value: "artisan", label: "Artisan / BTP" },
          { value: "service-pro", label: "Service pro", sub: "conseil, avocat, finance..." },
          { value: "sante", label: "Santé / bien-être" },
          { value: "assoc", label: "Association / éducation" },
          { value: "autre", label: "Autre" },
        ],
      },
      {
        id: "features",
        kind: "multi",
        label: "Fonctionnalités spécifiques",
        sub: "Sélectionnez tout ce qui s'applique",
        required: false,
        options: [
          { value: "blog", label: "Blog / actualités" },
          { value: "rdv", label: "Prise de rendez-vous" },
          { value: "multilang", label: "Plusieurs langues" },
          { value: "payment", label: "Paiement en ligne" },
          { value: "ai", label: "IA / chatbot" },
          { value: "crm", label: "CRM / intégrations" },
          { value: "autre", label: "Autre (à préciser)" },
          { value: "none", label: "Rien de spécial", exclusive: true },
        ],
      },
      {
        id: "timeline",
        kind: "radio",
        label: "Quel est votre délai ?",
        required: true,
        options: [
          { value: "urgent", label: "Urgent", sub: "Sous 2 semaines" },
          { value: "flexible", label: "Flexible", sub: "Dans 1-2 mois" },
          { value: "relaxed", label: "Pas pressé", sub: "On prend le temps" },
        ],
      },
      {
        id: "contact",
        kind: "contact",
        label: "Recevez votre devis",
        sub: "Un seul champ obligatoire : votre email.",
      },
    ],
    // Labels used in the contact step, success screen, privacy note, etc.
    form: {
      emailLabel: "Email",
      emailRequired: "*",
      emailPlaceholder: "vous@entreprise.fr",
      nameLabel: "Nom",
      nameSub: "optionnel",
      namePlaceholder: "Votre nom",
      companyLabel: "Entreprise",
      companySub: "optionnel",
      companyPlaceholder: "Nom de votre entreprise",
      noteLabel: "Note libre",
      noteSub: "optionnel",
      notePlaceholder:
        "Une précision sur votre projet, un lien, une question...",
      submitLabel: "Recevoir mon devis",
      submitLoadingLabel: "Envoi en cours...",
      privacy:
        "Vos informations restent confidentielles. Pas de spam, et nous ne revendons jamais votre email à des tiers.",
    },
    success: {
      title: "Merci, c'est reçu !",
      body:
        "Nous revenons vers vous sous 48h avec un devis personnalisé. Pensez à vérifier vos spams.",
      restartLabel: "Envoyer un autre projet",
    },
    switchToFreeform: "Je préfère décrire mon projet directement",
    switchToWizard: "Je me laisse guider",
    restartLabel: "Recommencer depuis le début",
    // Freeform mode: the classic form (name, email, company, message).
    freeform: {
      nameLabel: "Nom",
      nameRequired: "*",
      namePlaceholder: "Votre nom",
      emailLabel: "Email",
      emailRequired: "*",
      emailPlaceholder: "vous@entreprise.fr",
      companyLabel: "Entreprise",
      companySub: "optionnel",
      companyPlaceholder: "Nom de votre entreprise",
      messageLabel: "Votre projet",
      messageRequired: "*",
      messagePlaceholder: "Votre activité, vos objectifs, vos besoins...",
      submitLabel: "Envoyer",
      submitLoadingLabel: "Envoi en cours...",
    },
    errors: {
      title: "Une erreur est survenue",
      description: "Réessayez ou écrivez-nous directement à contact@lacrea.fr",
    },
  },

  // --- Contact form (legacy — kept for reference, no longer rendered) ---
  contact: {
    title: "Parlons de votre projet",
    subtitle:
      "Décrivez votre activité en quelques lignes. Réponse sous 48h, devis gratuit.",
    labels: {
      name: "Nom *",
      email: "Email *",
      company: "Entreprise",
      message: "Votre projet *",
    },
    placeholders: {
      name: "Votre nom",
      email: "vous@entreprise.fr",
      company: "Nom de votre entreprise",
      message: "Quelques mots sur votre activité et vos besoins",
    },
    submitLabel: "Envoyer",
    submitLoadingLabel: "Envoi en cours...",
    successTitle: "Message envoyé",
    successDescription: "On revient vers vous sous 48h.",
    errorTitle: "Une erreur est survenue",
    errorDescription: "Réessayez ou écrivez-nous à contact@lacrea.fr",
    privacy: "Vos informations restent confidentielles. Pas de spam, pas de revente.",
  },
};
